(() => {
  'use strict';
  const viewedKey = 'limarte:viewed:v1', searchKey = 'limarte:searches:v1';
  const day = 86400000, lifetime = 30 * day;
  const handleOK = v => typeof v === 'string' && /^[a-z0-9][a-z0-9_-]*$/i.test(v);
  const idOK = v => /^\d+$/.test(String(v || ''));
  const text = v => String(v || '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  const list = v => (Array.isArray(v) ? v : typeof v === 'string' ? [v] : []).filter(v => typeof v === 'string').slice(0, 40);
  const parse = (v, fallback = []) => { try { return JSON.parse(v); } catch (_) { return fallback; } };
  const read = key => { try { return parse(localStorage.getItem(key) || '[]'); } catch (_) { return []; } };
  const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) { /* This page's in-memory history still works. */ } };
  const fresh = t => Number.isFinite(t) && t <= Date.now() + 60000 && Date.now() - t < lifetime;
  const validViewed = items => {
    const seen = new Set();
    return (Array.isArray(items) ? items : []).filter(p => p && handleOK(p.handle) && idOK(p.id) && fresh(p.at) && !seen.has(p.handle) && seen.add(p.handle)).slice(0, 20);
  };
  const validSearches = items => (Array.isArray(items) ? items : []).filter(p => p && typeof p.query === 'string' && p.query.trim() && p.query.length <= 100 && fresh(p.at)).slice(0, 5);
  let viewed = validViewed(read(viewedKey)), searches = validSearches(read(searchKey));
  let recentVersion = 0, cartVersion = 0;
  const cache = new Map();
  function metadata(node) {
    const e = node?.matches?.('[data-product-interest]') ? node : node?.querySelector('[data-product-interest]');
    if (!e || !handleOK(e.dataset.handle) || !idOK(e.dataset.id)) return null;
    const tags = list(parse(e.dataset.tags));
    const tagged = prefix => tags.filter(t => new RegExp(`^(?:${prefix})\\s*:`, 'i').test(t)).map(t => t.slice(t.indexOf(':') + 1).trim());
    return {id:e.dataset.id, handle:e.dataset.handle, title:e.dataset.title || '', games:[...list(parse(e.dataset.games)), ...tagged('Game|Series')], characters:[...list(parse(e.dataset.characters)), ...tagged('Character')], collections:list(parse(e.dataset.collections))};
  }
  const rootURL = page => `${page?.dataset.wishlistRoot || page?.dataset.discoveryRoot || window.Shopify?.routes?.root || '/'}`.replace(/\/?$/, '/');
  async function request(url) {
    const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, {credentials:'same-origin', signal:controller.signal});
      if (!response.ok) return null;
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (_) { return null; }
    finally { clearTimeout(timer); }
  }
  async function cardFor(handle, root) {
    const key = root + handle;
    if (!cache.has(key)) cache.set(key, request(`${root}products/${encodeURIComponent(handle)}?view=browsed-card`).then(doc => {
      const card = doc?.querySelector('.product-card');
      if (card?.dataset.productHandle === handle) return card;
      cache.delete(key); return null;
    }));
    const card = await cache.get(key);
    return card ? document.importNode(card, true) : null;
  }
  async function mapLimited(items, callback) {
    const output = new Array(items.length); let next = 0;
    await Promise.all(Array.from({length:Math.min(4, items.length)}, async () => {
      while (next < items.length) { const i = next++; output[i] = await callback(items[i], i); }
    }));
    return output;
  }
  const refreshHearts = () => document.dispatchEvent(new Event('limarte:wishlist:refresh'));
  async function renderRecent(page) {
    const section = page.querySelector('[data-recently-viewed]'); if (!section) return;
    const version = ++recentVersion, grid = section.querySelector('[data-recently-viewed-grid]');
    grid.setAttribute('aria-busy', 'true');
    // Fetch current storefront cards rather than storing stale images or prices.
    const cards = (await mapLimited(viewed.slice(0, 8), p => cardFor(p.handle, rootURL(page)))).filter(Boolean);
    if (version !== recentVersion || !page.isConnected) return;
    grid.replaceChildren(...cards); grid.setAttribute('aria-busy', 'false'); section.hidden = !cards.length;
    refreshHearts();
  }
  const overlap = (a, b) => a.some(v => b.some(w => text(v) && text(v) === text(w)));
  const phrase = (haystack, needle) => needle && (` ${text(haystack)} `).includes(` ${text(needle)} `);
  function affinity(candidate, seed) {
    if (overlap(candidate.characters, seed.characters)) return 500;
    if (seed.characters.some(c => phrase(candidate.title, c))) return 450;
    if (overlap(candidate.games, seed.games)) return 300;
    if (seed.games.some(g => phrase(candidate.title, g))) return 250;
    if (overlap(candidate.collections, seed.collections)) return 40;
    return 0;
  }
  async function randomFallback(page, excluded) {
    const base = new URL(page.dataset.fallbackCollection || `${rootURL(page)}collections/all`, window.location.origin);
    if (base.origin !== window.location.origin) return [];
    const pool = new Map();
    const loadPage = async number => {
      const url = new URL(base); url.searchParams.set('view', 'cart-fallback'); url.searchParams.set('page', String(number));
      const doc = await request(url.href), scope = doc?.querySelector('[data-cart-fallback-products]');
      scope?.querySelectorAll('.product-card').forEach(card => {
        const product = metadata(card);
        if (product && !excluded.has(product.handle)) pool.set(product.handle, document.importNode(card, true));
      });
      return Number.parseInt(scope?.dataset.pages || '0', 10);
    };
    const pages = await loadPage(1);
    // Sample a real additional catalog page, rather than a fixed set of products.
    const visited = new Set([1]);
    if (Number.isFinite(pages) && pages > 1) {
      const extra = 2 + Math.floor(Math.random() * (pages - 1));
      visited.add(extra); await loadPage(extra);
      if (pool.size < 4 && pages > 2) {
        const other = extra === 2 ? 3 : 2;
        if (!visited.has(other)) await loadPage(other);
      }
    }
    const cards = [...pool.values()];
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1)); [cards[i], cards[j]] = [cards[j], cards[i]];
    }
    return cards.slice(0, 4);
  }
  async function renderCart(page) {
    const section = page.querySelector('[data-cart-recommendations]'); if (!section) return;
    const version = ++cartVersion, root = rootURL(page), grid = section.querySelector('[data-cart-recommendations-grid]');
    grid.setAttribute('aria-busy', 'true');
    const cart = [...page.querySelectorAll('.cart-item [data-product-interest]')].map(metadata).filter(Boolean);
    const excluded = new Set(cart.map(p => p.handle));
    const seeds = cart.slice(0, 2).map((p, i) => ({...p, weight:10000-i*100}));
    // Recent views get current metadata; newer interest outweighs older history.
    const history = (await mapLimited(viewed.slice(0, 2), async (p, i) => {
      const current = metadata(await cardFor(p.handle, root));
      return current ? {...current, weight:3000 / (1 + (Date.now()-p.at)/day) / (1+i*.15)} : null;
    })).filter(Boolean);
    for (const p of history) if (!seeds.some(s => s.handle === p.handle)) seeds.push(p);
    const latestSearch = searches[0];
    const queryWeight = latestSearch ? 3000 / (1+(Date.now()-latestSearch.at)/day) : 0;
    const queries = new Map();
    const addQuery = (q, weight, seed) => {
      if (typeof q !== 'string' || !q.trim() || q.length > 100) return;
      const key = text(q), old = queries.get(key);
      if (!old || weight > old.weight) queries.set(key, {query:q.trim(), weight, seed});
    };
    seeds.forEach(seed => {
      seed.characters.slice(0, 1).forEach(q => addQuery(q, seed.weight+500, seed));
      seed.games.slice(0, 1).forEach(q => addQuery(q, seed.weight+300, seed));
      // When structured fields are absent, search the authored title's subject.
      // This is a search hint, not an inferred Character/Game classification.
      if (!seed.characters.length && !seed.games.length) {
        const subject = seed.title.split(/\s+[–—-]\s+|\s*[–—]\s*/)[0].trim();
        addQuery(subject, seed.weight+100, seed);
      }
    });
    if (latestSearch) addQuery(latestSearch.query, queryWeight+500, null);
    history.forEach(p => excluded.add(p.handle));
    const queryJobs = [...queries.values()].sort((a,b) => b.weight-a.weight).slice(0, 4);
    // Always retain the most recent search even when cart metadata supplied many queries.
    if (latestSearch && !queryJobs.some(j => text(j.query) === text(latestSearch.query))) queryJobs.push(queries.get(text(latestSearch.query)));
    const candidates = new Map(); let order = 0;
    function offer(card, score) {
      const p = metadata(card); if (!p || excluded.has(p.handle)) return;
      const old = candidates.get(p.handle);
      if (!old || score > old.score) candidates.set(p.handle, {card, score, order:old?.order ?? order++});
    }
    await mapLimited([...seeds.map(seed => ({seed})), ...queryJobs.map(query => ({query}))], async job => {
      let doc;
      if (job.seed) {
        const params = new URLSearchParams({section_id:'cart-recommendation-products', product_id:job.seed.id, limit:'10', intent:'related'});
        doc = await request(`${root}recommendations/products?${params}`);
      } else {
        const params = new URLSearchParams({q:job.query.query, type:'product', view:'discovery-products', 'options[prefix]':'last'});
        doc = await request(`${root}search?${params}`);
      }
      const scope = doc?.querySelector(job.seed ? '[data-wishlist-recommendation-results]' : '[data-interest-search-results]');
      scope?.querySelectorAll('.product-card').forEach(card => {
        const p = metadata(card); if (!p) return;
        let score = job.seed ? job.seed.weight+25 : job.query.weight;
        seeds.forEach(seed => { const match = affinity(p, seed); if (match) score = Math.max(score, seed.weight+match); });
        if (latestSearch && (phrase(p.title, latestSearch.query) || [...p.games,...p.characters].some(v => phrase(v, latestSearch.query)))) score = Math.max(score, queryWeight+500);
        offer(document.importNode(card, true), score);
      });
    });
    if (version !== cartVersion || !page.isConnected) return;
    let cards = [...candidates.values()].sort((a,b) => b.score-a.score || a.order-b.order).slice(0, 4).map(p => p.card);
    // Random catalog cards are used only when no related candidate exists.
    // Even one related result is retained without unrelated filler.
    if (!cards.length && cart.length) cards = await randomFallback(page, new Set(cart.map(p => p.handle)));
    if (version !== cartVersion || !page.isConnected) return;
    grid.replaceChildren(...cards); grid.setAttribute('aria-busy', 'false'); section.hidden = !cards.length;
    refreshHearts();
  }
  function scan() {
    document.querySelectorAll('[data-viewed-product]').forEach(node => {
      if (node.dataset.viewTracked) return; node.dataset.viewTracked = 'true';
      const p = metadata(node); if (!p) return;
      viewed = [{...p, at:Date.now()}, ...viewed.filter(old => old.handle !== p.handle)].slice(0,20); write(viewedKey, viewed);
    });
    document.querySelectorAll('[data-interest-search]').forEach(node => {
      if (node.dataset.searchTracked) return; node.dataset.searchTracked = 'true';
      const query = node.dataset.interestSearch.trim().slice(0,100); if (!query) return;
      searches = [{query,at:Date.now()}, ...searches.filter(p => text(p.query) !== text(query))].slice(0,5); write(searchKey, searches);
    });
    document.querySelectorAll('[data-wishlist-page]').forEach(renderRecent);
    document.querySelectorAll('[data-cart-discovery]').forEach(renderCart);
  }
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-clear-viewed]')) return;
    viewed = []; write(viewedKey, viewed);
    document.querySelectorAll('[data-wishlist-page]').forEach(renderRecent);
  });
  window.addEventListener('storage', event => {
    if (event.key === viewedKey || event.key === null) viewed = validViewed(read(viewedKey));
    if (event.key === searchKey || event.key === null) searches = validSearches(read(searchKey));
    if (![viewedKey,searchKey,null].includes(event.key)) return;
    document.querySelectorAll('[data-wishlist-page]').forEach(renderRecent);
    document.querySelectorAll('[data-cart-discovery]').forEach(renderCart);
  });
  window.addEventListener('pageshow', event => { if (event.persisted) { viewed=validViewed(read(viewedKey)); searches=validSearches(read(searchKey)); scan(); } });
  document.addEventListener('shopify:section:load', scan);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
})();
